import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const BANNER_PAGES = [
  { key: 'home', label: 'Home' },
  { key: 'real-estate', label: 'Real Estate' },
  { key: 'commercial', label: 'Commercial' },
  { key: 'social-media', label: 'Social Media' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'contact', label: 'Contact' },
  { key: 'portal', label: 'Client Portal' },
];

// Xero Connection Status Component
function XeroConnectionStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkXeroStatus();
  }, []);

  const checkXeroStatus = async () => {
    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/xero/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error checking Xero status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero?')) return;

    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      await fetch(`${backendUrl}/api/admin/xero/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await checkXeroStatus();
    } catch (error) {
      alert('Failed to disconnect Xero');
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-600">Checking connection...</div>;
  }

  if (status?.connected) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
            <div>
              <p className="font-semibold text-green-800">Connected to Xero</p>
              <p className="text-xs text-green-700 mt-0.5">Invoices will be auto-created when bookings are marked as completed</p>
            </div>
          </div>
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-center space-x-3">
        <i className="ri-error-warning-line text-yellow-600 text-xl"></i>
        <div>
          <p className="font-semibold text-yellow-800">Not Connected</p>
          <p className="text-xs text-yellow-700 mt-0.5">Click "Connect to Xero" below to authorize</p>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    google_calendar: {
      client_id: '',
      client_secret: '',
      calendar_id: 'primary',
      business_hours: {
        monday: { enabled: true, start: '09:00', end: '18:00' },
        tuesday: { enabled: true, start: '09:00', end: '18:00' },
        wednesday: { enabled: true, start: '09:00', end: '18:00' },
        thursday: { enabled: true, start: '09:00', end: '18:00' },
        friday: { enabled: true, start: '09:00', end: '18:00' },
        saturday: { enabled: true, start: '10:00', end: '16:00' },
        sunday: { enabled: false, start: '00:00', end: '00:00' }
      },
      buffer_minutes: 30,
      package_durations: {
        'Photography Only': 60,
        'Videography Only': 120,
        'Combo Package': 120
      }
    },
    xero: {
      client_id: '',
      client_secret: '',
      tenant_id: '',
      auto_invoice: false
    },
    banner: {
      enabled: false,
      message: '',
      type: 'info',
      pages: [],
      dismissible: true,
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load settings');
      
      const data = await response.json();
      if (data.google_calendar) {
        // Ensure package_durations exists
        if (!data.google_calendar.package_durations) {
          data.google_calendar.package_durations = {
            'Photography Only': 60,
            'Package 1': 120,
            'Package 2': 120,
            'Package 3': 180
          };
        }
        setSettings(prev => ({
          ...prev,
          ...data,
          banner: { ...prev.banner, ...(data.banner || {}) },
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('admin_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) throw new Error('Failed to save settings');
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w" alt="Logo" className="h-10 w-10" />
              <h1 className="text-xl font-bold text-gray-900">Settings</h1>
            </div>
            <button onClick={handleLogout} className="text-red-600 hover:text-red-700">
              <i className="ri-logout-box-line"></i> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        <div className="w-64 bg-white h-screen shadow-sm">
          <nav className="p-4 space-y-2">
            <Link to="/admin/dashboard" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-dashboard-line text-xl"></i><span>Dashboard</span>
            </Link>
            <Link to="/admin/bookings" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-calendar-check-line text-xl"></i><span>Bookings</span>
            </Link>
            <Link to="/admin/contacts" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-message-3-line text-xl"></i><span>Contacts</span>
            </Link>
            <Link to="/admin/reviews" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-star-line text-xl"></i><span>Reviews</span>
            </Link>
            <Link to="/admin/settings" className="flex items-center space-x-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg font-medium">
              <i className="ri-settings-3-line text-xl"></i><span>Settings</span>
            </Link>
          </nav>
        </div>

        <div className="flex-1 p-8 max-w-5xl">
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {message.text}
            </div>
          )}

          {/* Site Announcement Banner */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Site Announcement Banner</h3>
                <p className="text-sm text-gray-600 mt-1">Show a message bar at the top of the site (e.g. "Closed for the holidays" or "Booked out until March")</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer shrink-0" data-testid="banner-enabled-toggle">
                <span className="text-sm font-medium text-gray-700">{settings.banner.enabled ? 'On' : 'Off'}</span>
                <span
                  role="switch"
                  aria-checked={settings.banner.enabled}
                  onClick={() => setSettings({ ...settings, banner: { ...settings.banner, enabled: !settings.banner.enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.banner.enabled ? 'bg-teal-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.banner.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </span>
              </label>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Announcement message</label>
                <textarea
                  rows="2"
                  value={settings.banner.message}
                  onChange={(e) => setSettings({ ...settings, banner: { ...settings.banner, message: e.target.value } })}
                  placeholder="e.g. We're closed from 24 Dec - 2 Jan. Bookings resume 3 Jan."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="banner-message-input"
                />
              </div>

              <div className="flex flex-wrap gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
                  <select
                    value={settings.banner.type}
                    onChange={(e) => setSettings({ ...settings, banner: { ...settings.banner, type: e.target.value } })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="banner-type-select"
                  >
                    <option value="info">Info (teal)</option>
                    <option value="warning">Warning (amber)</option>
                    <option value="urgent">Urgent / Closed (red)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visitor can dismiss?</label>
                  <label className="flex items-center gap-2 mt-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.banner.dismissible}
                      onChange={(e) => setSettings({ ...settings, banner: { ...settings.banner, dismissible: e.target.checked } })}
                      className="rounded"
                      data-testid="banner-dismissible-checkbox"
                    />
                    <span className="text-sm text-gray-700">Show a close (×) button</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Show on these pages</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {BANNER_PAGES.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={settings.banner.pages.includes(key)}
                        onChange={(e) => {
                          const pages = e.target.checked
                            ? [...settings.banner.pages, key]
                            : settings.banner.pages.filter((p) => p !== key);
                          setSettings({ ...settings, banner: { ...settings.banner, pages } });
                        }}
                        className="rounded"
                        data-testid={`banner-page-${key}`}
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {settings.banner.message && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div className={`rounded-lg px-4 py-2.5 text-white text-sm font-medium flex items-center gap-2 ${
                    settings.banner.type === 'urgent' ? 'bg-rose-700' : settings.banner.type === 'warning' ? 'bg-amber-600' : 'bg-teal-700'
                  }`} data-testid="banner-preview">
                    <i className={settings.banner.type === 'urgent' ? 'ri-alarm-warning-line' : settings.banner.type === 'warning' ? 'ri-error-warning-line' : 'ri-information-line'}></i>
                    <span className="flex-1">{settings.banner.message}</span>
                    {settings.banner.dismissible && <i className="ri-close-line"></i>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Google Calendar Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Google Calendar Integration</h3>
              <p className="text-sm text-gray-600 mt-1">Configure Google Calendar for booking management</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
                <input
                  type="text"
                  value={settings.google_calendar.client_id}
                  onChange={(e) => setSettings({...settings, google_calendar: {...settings.google_calendar, client_id: e.target.value}})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your-client-id.apps.googleusercontent.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
                <input
                  type="password"
                  value={settings.google_calendar.client_secret}
                  onChange={(e) => setSettings({...settings, google_calendar: {...settings.google_calendar, client_secret: e.target.value}})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="GOCSPX-xxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Calendar ID</label>
                <input
                  type="text"
                  value={settings.google_calendar.calendar_id}
                  onChange={(e) => setSettings({...settings, google_calendar: {...settings.google_calendar, calendar_id: e.target.value}})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="primary"
                />
                <p className="text-xs text-gray-500 mt-1">Use primary for your main calendar</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Buffer Time (minutes)</label>
                <input
                  type="number"
                  value={settings.google_calendar.buffer_minutes}
                  onChange={(e) => setSettings({...settings, google_calendar: {...settings.google_calendar, buffer_minutes: parseInt(e.target.value)}})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="30"
                />
                <p className="text-xs text-gray-500 mt-1">Time added after each booking for setup/travel</p>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('admin_token');
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    try {
                      const response = await fetch(`${backendUrl}/api/admin/google/auth-url`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json();
                      if (data.authorization_url) {
                        window.location.href = data.authorization_url;
                      }
                    } catch (error) {
                      alert('Please save your Client ID and Secret first');
                    }
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all flex items-center justify-center space-x-2"
                >
                  <i className="ri-google-fill text-xl"></i>
                  <span>Connect Google Calendar</span>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">Save credentials above then click to authorize</p>
              </div>
            </div>
          </div>

          {/* Package Durations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Package Durations</h3>
              <p className="text-sm text-gray-600 mt-1">Set how long each package session takes</p>
            </div>
            <div className="p-6 space-y-4">
              {Object.entries(settings.google_calendar.package_durations || {}).map(([packageName, duration]) => (
                <div key={packageName} className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 flex-1">{packageName}</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      value={duration}
                      onChange={(e) => setSettings({
                        ...settings,
                        google_calendar: {
                          ...settings.google_calendar,
                          package_durations: {
                            ...settings.google_calendar.package_durations,
                            [packageName]: parseInt(e.target.value) || 60
                          }
                        }
                      })}
                      min="30"
                      step="30"
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-600">minutes</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Business Hours</h3>
              <p className="text-sm text-gray-600 mt-1">Set your available booking hours for each day</p>
            </div>
            <div className="p-6 space-y-3">
              {Object.entries(settings.google_calendar.business_hours).map(([day, hours]) => (
                <div key={day} className="flex items-center space-x-4">
                  <div className="w-32">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={hours.enabled}
                        onChange={(e) => setSettings({
                          ...settings,
                          google_calendar: {
                            ...settings.google_calendar,
                            business_hours: {
                              ...settings.google_calendar.business_hours,
                              [day]: { ...hours, enabled: e.target.checked }
                            }
                          }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-700 capitalize">{day}</span>
                    </label>
                  </div>
                  <input
                    type="time"
                    value={hours.start}
                    onChange={(e) => setSettings({
                      ...settings,
                      google_calendar: {
                        ...settings.google_calendar,
                        business_hours: {
                          ...settings.google_calendar.business_hours,
                          [day]: { ...hours, start: e.target.value }
                        }
                      }
                    })}
                    disabled={!hours.enabled}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="time"
                    value={hours.end}
                    onChange={(e) => setSettings({
                      ...settings,
                      google_calendar: {
                        ...settings.google_calendar,
                        business_hours: {
                          ...settings.google_calendar.business_hours,
                          [day]: { ...hours, end: e.target.value }
                        }
                      }
                    })}
                    disabled={!hours.enabled}
                    className="px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Xero Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Xero Integration</h3>
              <p className="text-sm text-gray-600 mt-1">Auto-generate and send invoices for completed bookings</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div className="flex-1 text-sm text-blue-800">
                    <p className="font-semibold mb-1">How to connect Xero:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Create an app at <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" className="underline">developer.xero.com/app/manage</a></li>
                      <li>Use redirect URI: <code className="bg-white px-2 py-0.5 rounded text-xs">https://minchiobrothers.au/api/admin/xero/callback</code></li>
                      <li>Copy your Client ID and Secret below, save settings, then click "Connect to Xero"</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <XeroConnectionStatus />
              </div>

              <div className="pt-4">
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('admin_token');
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    try {
                      const response = await fetch(`${backendUrl}/api/admin/xero/auth-url`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json();
                      if (data.authorization_url) {
                        window.location.href = data.authorization_url;
                      }
                    } catch (error) {
                      alert('Error connecting to Xero. Please try again.');
                    }
                  }}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center space-x-2"
                >
                  <i className="ri-link text-xl"></i>
                  <span>Connect to Xero</span>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">Authorize Minchio Brothers to create invoices in your Xero account</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
